import cx from 'classnames';
import React from 'react';
import { useFileFlippingCallbacks } from 'js/file_viewer/api/navigation';
import { useAreToolsHidden, useIsFullscreen } from 'js/file_viewer/api/ui';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { areToolsHiddenAtom, isSlideshowAtom, effectiveSlideshowDelayAtom, } from 'js/file_viewer/core/atoms/ui/general';
import { useFileViewerAtomValue, useSetFileViewerAtom } from 'js/file_viewer/core/atoms/use_atom';
import { UserAction, UserActionContext } from 'js/file_viewer/core/logging/constants';
import { useCurrentPreviewMetadata } from 'js/file_viewer/core/queries/preview_metadata';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { useRootPreviewKey } from 'js/file_viewer/hooks/selectors';
import { useTouchInteraction } from 'js/file_viewer/hooks/useTouchInteraction';
import { LoadingIndicator } from 'js/file_viewer/loading_indicator/loading_indicator';
import { useToggleFullscreenOnTouch } from 'js/file_viewer/mobile_web/utils';
import * as css from 'js/file_viewer/preview_content/preview_content.module.css';
import { FullscreenRightSection } from 'js/file_viewer/ui/fullscreen_right_section';
import { ShowToolsButton } from 'js/file_viewer/ui/show_tools_button';
import { useSlideshowFlipping } from 'js/file_viewer/utils/slideshow';
const useMaybeSetFullscreenUiHiddenFromPreferences = () => {
    const { optionalIOClients: { preferencesClient }, logUserAction, } = useFileViewerContext();
    const setAreToolsHidden = useSetFileViewerAtom(areToolsHiddenAtom);
    const isFullscreen = useIsFullscreen();
    React.useEffect(() => {
        var _a, _b;
        if (!isFullscreen) {
            return;
        }
        const isHidden = (_b = (_a = preferencesClient === null || preferencesClient === void 0 ? void 0 : preferencesClient.ui) === null || _a === void 0 ? void 0 : _a.toolsAreHiddenInFullscreen()) !== null && _b !== void 0 ? _b : false;
        logUserAction(isHidden ? UserAction.HideTools : UserAction.ShowTools, UserActionContext.UserPreference);
        setAreToolsHidden(isHidden);
    }, [isFullscreen, logUserAction, preferencesClient === null || preferencesClient === void 0 ? void 0 : preferencesClient.ui, setAreToolsHidden]);
};
export const PreviewContent = ({ children }) => {
    const { featureConfig } = useFileViewerContext();
    const rootPreviewKey = useRootPreviewKey();
    const isSlideShow = useFileViewerAtomValue(isSlideshowAtom);
    const slideshowDelayMS = useFileViewerAtomValue(effectiveSlideshowDelayAtom);
    const isFullScreen = useIsFullscreen();
    const areToolsHidden = useAreToolsHidden();
    const previewMetadata = useCurrentPreviewMetadata();
    const isPreviewLoading = previewMetadata.previewType === PreviewType.Loading;
    const isPreviewVideo = previewMetadata.previewType === PreviewType.Video;
    const isMobileWeb = featureConfig.mobile_web;
    // On mobile web, make preview content selectable so screen readers can enter and exit fullscreen
    const tabIndex = isMobileWeb ? { tabIndex: 0 } : {};
    useMaybeSetFullscreenUiHiddenFromPreferences();
    const toggleFullscreen = useToggleFullscreenOnTouch();
    const { flipToNextFile, flipToPreviousFile, flipToNextSlideshowFile } = useFileFlippingCallbacks();
    useSlideshowFlipping(() => {
        flipToNextSlideshowFile(UserActionContext.Slideshow);
    }, isSlideShow && !isPreviewVideo ? slideshowDelayMS : undefined, rootPreviewKey);
    const { handleTouchStart, handleTouchEnd, handleClick } = useTouchInteraction({
        onSwipeLeft: () => flipToNextFile(UserActionContext.SurfaceGesture),
        onSwipeRight: () => flipToPreviousFile(UserActionContext.SurfaceGesture),
        onTap: toggleFullscreen,
    });
    return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    React.createElement("div", { className: css.previewContentWrapper, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, onClick: handleClick, ...tabIndex },
        isPreviewLoading ? React.createElement(LoadingIndicator, null) : null,
        React.createElement("div", { className: cx(css.previewContent, {
                [css.hidden]: isPreviewLoading,
            }) },
            areToolsHidden && !isMobileWeb ? (isFullScreen ? (React.createElement(FullscreenRightSection, null)) : (React.createElement(ShowToolsButton, null))) : null,
            children)));
};
PreviewContent.displayName = "PreviewContent"
//# sourceMappingURL=preview_content.preenvesnextjs.map
